import React, { useState } from 'react';
import { MapPin, Clock, Edit, Trash2, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Shop } from '../types/Shop';
import { CreateCustomShopData } from '../types/User';
import { formatTimeRemaining } from '../utils/shop';
import TagBadge from './TagBadge';
import ShopModal from './ShopModal';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from "@/components/ui/responsive-modal";
import { cn } from "@/lib/utils";

interface MyShopsModalProps {
  isOpen: boolean;
  onClose: () => void;
  shops: Shop[];
  isDarkMode: boolean;
  onShopDeleted?: (deletedShopId: string) => void;
  onShopUpdated?: () => void;
  showFilters?: boolean;
}

export default function MyShopsModal({
  isOpen,
  onClose,
  shops,
  isDarkMode = false,
  onShopDeleted,
  onShopUpdated,
}: MyShopsModalProps) {
  const [editingShop, setEditingShop] = useState<Shop | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  // Update a shop
  const handleUpdateShop = async (updatedShop: Shop | CreateCustomShopData) => {
    const shopData = updatedShop as Shop;
    if (!user || !shopData.id) return;

    try {
      setIsSubmitting(true);
      const token = await user.getIdToken();
      const response = await fetch(`/api/shops/${shopData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(shopData)
      });

      if (response.ok) {
        setEditingShop(null);
        if (onShopUpdated) {
          await onShopUpdated();
        }
      } else {
        alert('Failed to update shop');
      }
    } catch (error) {
      console.error('Error updating shop:', error);
      alert('Error updating shop');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete a shop
  const deleteShop = async (shopId: string) => {
    if (!user) return;
    if (!confirm('Are you sure you want to delete this shop?')) return;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`/api/shops/${shopId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        if (typeof onShopDeleted === 'function') {
          onShopDeleted(shopId);
        }
      } else {
        alert('Failed to delete shop');
      }
    } catch (error) {
      console.error('Error deleting shop:', error);
      alert('Error deleting shop');
    }
  };

  // Extend shop lifetime
  const extendLifetime = async (shopId: string, hours: number) => {
    if (!user) return;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`/api/shops/${shopId}/extend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ hours })
      });

      if (response.ok) {
        if (onShopUpdated) {
          await onShopUpdated();
        }
      } else {
        alert('Failed to extend shop lifetime');
      }
    } catch (error) {
      console.error('Error extending shop lifetime:', error);
      alert('Error extending shop lifetime');
    }
  };

  return (
    <>
      <ResponsiveModal open={isOpen} onOpenChange={onClose}>
        <ResponsiveModalContent className={cn(
          "max-h-[85vh] overflow-hidden flex flex-col",
          isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white"
        )}>
          <ResponsiveModalHeader className={cn(
            "border-b pb-4",
            isDarkMode ? "border-gray-700" : "border-gray-200"
          )}>
            <ResponsiveModalTitle className={isDarkMode ? "text-white" : ""}>
              My Shops {user ? `(${shops.length})` : ''}
            </ResponsiveModalTitle>
          </ResponsiveModalHeader>

          {/* Scrollable Content */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4"
          >
            {!user ? (
              <div className="text-center py-12">
                <h3 className={cn(
                  "text-lg font-medium mb-2",
                  isDarkMode ? "text-white" : "text-gray-900"
                )}>
                  Please log in to manage your shops
                </h3>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            ) : shops.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🏪</div>
                <h3 className={cn(
                  "text-lg font-medium mb-2",
                  isDarkMode ? "text-white" : "text-gray-900"
                )}>
                  No custom shops yet
                </h3>
                <p className={cn(
                  "mb-4",
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                )}>
                  Create your first custom shop to get started
                </p>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Go Back to Map
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {shops.map((shop) => {
                  const isExpired = shop.expiresAt && new Date(shop.expiresAt) <= new Date();

                  return (
                    <div
                      key={shop.id}
                      className={cn(
                        "rounded-lg border p-4",
                        isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200",
                        isExpired && "opacity-75"
                      )}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className={cn(
                              "font-bold text-lg",
                              isDarkMode ? "text-white" : "text-gray-900"
                            )}>
                              {shop.name}
                            </h3>
                            {isExpired && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                                Expired
                              </span>
                            )}
                          </div>
                          <TagBadge
                            tag={shop.type === "custom" ? "Custom Shop" : "Flea Market"}
                            type={shop.type}
                          />
                        </div>
                      </div>

                      {shop.description && (
                        <p className={cn(
                          "text-sm mb-3",
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        )}>
                          {shop.description}
                        </p>
                      )}

                      <div className="space-y-2 text-sm">
                        <div className={cn(
                          "flex items-center space-x-2",
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        )}>
                          <MapPin className="h-4 w-4 flex-shrink-0" />
                          <span className="flex-1">{shop.address}</span>
                        </div>

                        {shop.expiresAt && (
                          <div className={cn(
                            "flex items-center space-x-2",
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          )}>
                            <Clock className="h-4 w-4 flex-shrink-0" />
                            <span>
                              {isExpired ? 'Expired' : `Expires in ${formatTimeRemaining(shop.expiresAt)}`}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className={cn(
                        "flex items-center space-x-3 mt-4 pt-3 border-t",
                        isDarkMode ? "border-gray-700" : "border-gray-100"
                      )}>
                        <button
                          onClick={() => setEditingShop(shop)}
                          className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                        >
                          <Edit className="h-4 w-4" />
                          <span>Edit</span>
                        </button>

                        <button
                          onClick={() => deleteShop(shop.id!)}
                          className="flex items-center space-x-2 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span>Delete</span>
                        </button>

                        {!isExpired && shop.expiresAt && (
                          <button
                            onClick={() => extendLifetime(shop.id!, 24)}
                            className={cn(
                              "flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                              isDarkMode
                                ? "bg-gray-700 text-white hover:bg-gray-600"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            )}
                          >
                            <Plus className="h-4 w-4" />
                            <span>+24h</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Shop Modal for Editing */}
      <ShopModal
        isOpen={!!editingShop}
        onClose={() => setEditingShop(null)}
        onSave={handleUpdateShop}
        shop={editingShop}
        isDarkMode={isDarkMode}
        isSubmitting={isSubmitting}
      />
    </>
  );
}